/* eslint-disable react/no-multi-comp */
import {
  Avatar,
  colors,
  Divider,
  Drawer,
  Hidden,
  Link,
  List,
  ListSubheader,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { matchPath, useLocation } from 'react-router';
import { Link as RouterLink } from 'react-router-dom';
import NavItem from 'src/components/NavItem';
import {
  getDataFromSessionStorage,
  getNameInitials,
  userHaveAccess,
} from 'src/utils/general.utils';
import navConfig from './navConfig';

const useStyles = makeStyles(theme => ({
  root: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  mobileDrawer: {
    width: 256,
  },
  desktopDrawer: {
    width: 256,
    top: 64,
    height: 'calc(100% - 64px)',
  },
  navigation: {
    overflow: 'auto',
    padding: theme.spacing(0, 2, 2, 2),
    flexGrow: 1,

    '&::-webkit-scrollbar': {
      width: '3px',
    },

    /* Track */
    '&::-webkit-scrollbar-track': {
      margin: '9px',
    },

    /* Handle */
    '&::-webkit-scrollbar-thumb': {
      background: '#88888852',
    },

    /* Handle on hover */
    '&::-webkit-scrollbar-thumb:hover': {
      background: '#555',
    },
  },
  profile: {
    padding: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
  },
  badge: {
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
  },
  badgeDot: {
    height: 9,
    minWidth: 9,
  },
  onlineBadge: {
    backgroundColor: colors.green[600],
  },
  awayBadge: {
    backgroundColor: colors.orange[600],
  },
  busyBadge: {
    backgroundColor: colors.red[600],
  },
  offlineBadge: {
    backgroundColor: colors.grey[300],
  },
  avatar: {
    cursor: 'pointer',
    width: 64,
    height: 64,
  },
  details: {
    marginTop: theme.spacing(2),
    textAlign: 'center',
  },
  moreButton: {
    marginLeft: 'auto',
    color: colors.blueGrey[200],
  },
  divider: {
    marginRight: theme.spacing(2),
    marginLeft: theme.spacing(2),
  },
  footer: {
    padding: '16px 18px 12px',
  },
}));

function renderNavItems({
  // eslint-disable-next-line react/prop-types
  items,
  subheader,
  href,
  key,
  color,
  roles,
  ...rest
}) {
  const disabled = hasPermission(roles);
  return !disabled ? (
    <List key={key}>
      {subheader && (
        <ListSubheader disableSticky style={{ paddingLeft: '0px', color }}>
          {href ? (
            <Link href={href} style={{ color, textDecoration: 'none' }}>
              {subheader.toUpperCase()}
            </Link>
          ) : (
            subheader.toUpperCase()
          )}
        </ListSubheader>
      )}
      {/* eslint-disable-next-line react/prop-types */}
      {items.reduce(
        // eslint-disable-next-line no-use-before-define
        (acc, item) => reduceChildRoutes({ acc, item, ...rest }),
        []
      )}
    </List>
  ) : (
    <></>
  );
}

function reduceChildRoutes({ acc, pathname, item, depth = 0 }) {
  if (item.items) {
    const disabled = hasPermission(item.roles);
    const open = matchPath(pathname, {
      path: item.href,
      exact: false,
    });

    acc.push(
      <NavItem
        depth={depth}
        icon={item.icon}
        key={item.href}
        label={item.label}
        open={Boolean(open)}
        title={item.title}
        disabled={disabled}
      >
        {renderNavItems({
          depth: depth + 1,
          pathname,
          items: item.items,
        })}
      </NavItem>
    );
  } else {
    const disabled = hasPermission(item.roles);
    acc.push(
      <NavItem
        depth={depth}
        href={item.href}
        icon={item.icon}
        key={item.href}
        label={item.label}
        title={item.title}
        disabled={disabled}
      />
    );
  }

  return acc;
}

function hasPermission(roles) {
  if (!userHaveAccess(roles)) {
    return true;
  }
  return false;
}

function NavBar({ openMobile, onMobileClose, className, ...rest }) {
  const classes = useStyles();
  const location = useLocation();
  // eslint-disable-next-line no-unused-vars
  const [status, setStatus] = useState('online');

  const handleStatusToggle = () => {
    const statusSeq = {
      online: 'away',
      away: 'busy',
      busy: 'offline',
      offline: 'online',
    };

    setStatus(prevStatus => statusSeq[prevStatus]);
  };

  useEffect(() => {
    if (openMobile && onMobileClose) {
      onMobileClose();
    }

    // eslint-disable-next-line
  }, [location.pathname]);

  const content = (
    <div {...rest} className={clsx(classes.root, className)}>
      <div className={classes.profile}>
        <Link
          component={RouterLink}
          to="/overview"
          variant="h5"
          color="textPrimary"
          underline="none"
        >
          <Avatar
            alt="Person"
            onClick={handleStatusToggle}
            className={classes.avatar}
            src={
              getDataFromSessionStorage('userInfo')
                ? getDataFromSessionStorage('userInfo').image
                : null
            }
          >
            {getDataFromSessionStorage('userInfo')
              ? getDataFromSessionStorage('userInfo').image
                ? null
                : getNameInitials(getDataFromSessionStorage('userInfo').userName)
              : null}
          </Avatar>
        </Link>
        <div className={classes.details}>
          <Link
            component={RouterLink}
            to="/overview"
            variant="h5"
            color="textPrimary"
            underline="none"
          >
            {getDataFromSessionStorage('userInfo')
              ? getDataFromSessionStorage('userInfo').userName
              : null}
          </Link>
          <Typography variant="body2">
            {getDataFromSessionStorage('userInfo')
              ? getDataFromSessionStorage('userInfo').email
              : null}
          </Typography>
        </div>
      </div>
      <Divider className={classes.divider} />
      <nav className={classes.navigation}>
        {navConfig.map(list =>
          renderNavItems({
            items: list.items,
            subheader: list.subheader,
            pathname: location.pathname,
            key: list.subheader,
            href: list.href,
            color: list.color,
            roles: list.roles,
          })
        )}
      </nav>
      <Divider className={classes.divider} />
      <div className={classes.footer}>
        <Typography variant="body2">© Copyright Frost Interactive Services Pvt. ltd.</Typography>
      </div>
    </div>
  );

  return (
    <>
      <Hidden lgUp>
        <Drawer
          anchor="left"
          classes={{
            paper: classes.mobileDrawer,
          }}
          onClose={onMobileClose}
          open={openMobile}
          variant="temporary"
        >
          {content}
        </Drawer>
      </Hidden>
      <Hidden mdDown>
        <Drawer
          anchor="left"
          classes={{
            paper: classes.desktopDrawer,
          }}
          open
          variant="persistent"
        >
          {content}
        </Drawer>
      </Hidden>
    </>
  );
}

NavBar.propTypes = {
  className: PropTypes.string,
  onMobileClose: PropTypes.func,
  openMobile: PropTypes.bool,
};

export default NavBar;
